import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Rewind, FastForward, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function EpisodePlayer({ episode }) {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [isVolumeHovered, setIsVolumeHovered] = useState(false);
  const [isProgressHovered, setIsProgressHovered] = useState(false);
  const [progressHoverPosition, setProgressHoverPosition] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    audio.play().catch(err => {
      console.error('Auto-play failed:', err);
      setIsPlaying(false);
    });
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Event listeners
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    
    // Set initial volume
    audio.volume = volume;
    audio.muted = muted;
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [muted, volume]);
  
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
      });
    }
  };
  
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    setMuted(!muted);
    audio.muted = !muted;
  };
  
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newVolume;
    }
  };
  
  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !progressRef.current) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    // Left-to-right progress bar: left side (x=0) = beginning, right side (x=width) = end
    const pos = (e.clientX - rect.left) / rect.width;
    const newTime = pos * duration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };
  
  const handleProgressHover = (e) => {
    if (!progressRef.current) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    // Left-to-right for hover indicator
    const pos = (e.clientX - rect.left) / rect.width;
    setProgressHoverPosition(pos);
  };
  
  const handleSkip = (seconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    audio.currentTime += seconds;
  };
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div dir="rtl" className="bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-md border border-white/20">
      <audio
        ref={audioRef}
        src={episode.audioUrl}
        preload="metadata"
        className="hidden"
      />
      
      {/* Episode Info */}
      <div className="mb-3 text-right">
        <h4 className="font-bold text-[#393737] text-sm truncate">{episode.title}</h4>
        <p className="text-xs text-[#393737]/70 truncate">{episode.show?.title}</p>
      </div>
      
      {/* Progress Bar */}
      <div 
        ref={progressRef}
        className="relative h-2 bg-[#784BCB]/20 rounded-full mb-2 cursor-pointer"
        onClick={handleProgressClick}
        onMouseMove={handleProgressHover}
        onMouseEnter={() => setIsProgressHovered(true)}
        onMouseLeave={() => setIsProgressHovered(false)}
      >
        {/* Current Progress */}
        <motion.div 
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#784BCB] to-[#E15E92] rounded-full"
          style={{ width: `${(currentTime / duration) * 100}%`, left: 0 }}
          animate={{ width: `${(currentTime / duration) * 100}%` }}
          transition={{ type: 'tween', duration: 0.1 }}
        />
        
        {/* Hover Indicator */}
        <AnimatePresence>
          {isProgressHovered && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: -30 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-0 bg-[#784BCB] text-white text-xs py-1 px-2 rounded pointer-events-none"
              style={{ left: `calc(${progressHoverPosition * 100}% - 20px)` }}
            >
              {formatTime(duration * progressHoverPosition)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Time Display */}
      <div className="flex justify-between text-xs text-[#393737]/70 mb-3">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
      
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Volume Control */}
          <div className="relative flex items-center">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={toggleMute}
              onMouseEnter={() => setIsVolumeHovered(true)}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#784BCB]/10"
            >
              {muted ? (
                <VolumeX className="w-3.5 h-3.5 text-[#393737]" />
              ) : (
                <Volume2 className="w-3.5 h-3.5 text-[#784BCB]" />
              )}
            </motion.button>
            
            <AnimatePresence>
              {isVolumeHovered && (
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 60, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  className="absolute left-7 bg-white rounded-full overflow-hidden shadow-md"
                  onMouseEnter={() => setIsVolumeHovered(true)}
                  onMouseLeave={() => setIsVolumeHovered(false)}
                >
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-full h-1.5 appearance-none bg-[#784BCB]/20 accent-[#784BCB]"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Fast Forward Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => handleSkip(10)}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#784BCB]/10"
          >
            <FastForward className="w-3.5 h-3.5 text-[#784BCB]" />
          </motion.button>
          
          {/* Play/Pause Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={togglePlay}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-l from-[#784BCB] to-[#E15E92] text-white"
          >
            {isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 mr-0.5" />
            )}
          </motion.button>
          
          {/* Rewind Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => handleSkip(-10)}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#784BCB]/10"
          >
            <Rewind className="w-3.5 h-3.5 text-[#784BCB]" />
          </motion.button>
        </div>
      </div>
    </div>
  );
} 